import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Chart} from "primereact/chart";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import Icon from "../../../system/components/icon";
import {useTranslate} from "../../../system/useTranslate";
import {goalProgressApi} from "../../../models/goal/goal-progress-api";
import {DayPicker} from "../../projects/meetings/meetings-ui";
import GoalProgressSettings from "./goal-progress-settings";
import {PROJECT_MEASURES, SOURCES, UNITS, detailText, errorText, formatPercent, formatValue, manualPercent, parseNumber} from "./goal-progress-labels";
import "./goal-progress.css";

// The progress part of a goal's page (boss's task «تغییر تنظیمات منبع پیشرفت هدف», 2026-10-26;
// docs/tasks/28-goal-progress-source.md): «تکمیل هدف», «مقدار فعلی», «آخرین وضعیت», the chart (expected line from the
// start to the target, the actual line, today) and, for a manual goal, «به‌روزرسانی پیشرفت».

const CHART_FONT = "Shabnam-FD, IranYekan, Tahoma, sans-serif";
const DAY = 86400000;
const dayMs = (day) => Date.parse(`${day}T00:00:00Z`);
const isoOf = (ms) => new Date(ms).toISOString().slice(0, 10);
const shortDate = (day) => new Intl.DateTimeFormat("fa-IR-u-ca-persian", {day: "numeric", month: "short", timeZone: "UTC"}).format(new Date(dayMs(day)));
const fullDate = (day) => new Intl.DateTimeFormat("fa-IR-u-ca-persian", {day: "numeric", month: "long", year: "numeric", timeZone: "UTC"}).format(new Date(dayMs(day)));

// the vertical «امروز» line
const todayLine = {
    id: "goalProgressToday",
    afterDatasetsDraw(chart, args, options) {
        const index = options?.index;
        if (index === undefined || index < 0) return;
        const x = chart.scales.x.getPixelForValue(index);
        const {top, bottom} = chart.chartArea;
        const ctx = chart.ctx;
        ctx.save();
        ctx.strokeStyle = "#6366f1";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.stroke();
        ctx.fillStyle = "#6366f1";
        ctx.font = `11px ${CHART_FONT}`;
        ctx.textAlign = "center";
        ctx.fillText("امروز", x, top - 4);
        ctx.restore();
    },
};

const PERIODS = 5;
const ACTUAL_COLOR = "#E6531E";
const PLAN_COLOR = "#14b8a6";

// The «روند تحقق هدف» chart (boss's task «نمودار پیشرفت در هدف», 2026-10-26; docs/tasks/29-goal-progress-chart.md):
// percent on both lines so plan and reality compare directly. Points: the start, equal periods, today and the end (the
// due date). The plan rises evenly from 0% at the start to 100% at the due date; the actual line is the recorded history
// up to today's percent. Without a due date there is no plan to compare with, so no plan line is drawn.
export function chartSeries(progress) {
    const manual = progress?.settings?.source === "manual" ? progress.settings.manual : null;
    const from = progress.span.from;
    const hasPlan = Boolean(progress.end && progress.end > from);
    const end = hasPlan ? progress.end : progress.span.to;
    const today = progress.today;
    const length = Math.max(DAY, dayMs(end) - dayMs(from));

    const points = [{day: from, label: "شروع", kind: "start"}];
    for (let index = 1; index < PERIODS; index += 1) {
        points.push({day: isoOf(dayMs(from) + Math.round((length * index) / PERIODS / DAY) * DAY), label: `دوره ${index.toLocaleString("fa-IR")}`, kind: "period"});
    }
    points.push({day: end, label: hasPlan ? "پایان" : "پایان (بدون موعد)", kind: "end"});
    if (today > from) {
        const same = points.find((point) => point.day === today);
        if (same) same.label = `${same.label} · امروز`;
        else points.push({day: today, label: "امروز", kind: "today"});
    }
    points.sort((a, b) => a.day.localeCompare(b.day));

    const entries = [...(progress.entries || [])].sort((a, b) => a.day.localeCompare(b.day));
    const planned = points.map((point) => (hasPlan ? Math.round(Math.min(100, Math.max(0, ((dayMs(point.day) - dayMs(from)) / length) * 100)) * 10) / 10 : null));
    const recorded = points.map((point) => {
        if (point.day > today) return null;
        if (point.day === today && progress.percent !== null && progress.percent !== undefined) {
            return {percent: Number(progress.percent), value: manual ? manual.current : null};
        }
        let found = null;
        for (const entry of entries) {
            if (entry.day <= point.day) found = entry;
            else break;
        }
        return found ? {percent: found.percent ?? null, value: manual ? found.value : null} : null;
    });
    const todayIndex = points.findIndex((point) => point.day === today);
    const actualToday = progress.percent === null || progress.percent === undefined ? null : Number(progress.percent);
    const plannedToday = hasPlan ? Math.round(Math.min(100, Math.max(0, ((dayMs(today) - dayMs(from)) / length) * 100)) * 10) / 10 : null;
    return {
        points,
        labels: points.map((point) => point.label),
        planned,
        actual: recorded.map((item) => (item ? item.percent : null)),
        values: recorded.map((item) => (item ? item.value : null)),
        todayIndex,
        hasPlan,
        plannedToday,
        actualToday,
        gap: plannedToday !== null && actualToday !== null ? Math.round((plannedToday - actualToday) * 10) / 10 : null,
    };
}

// «برداشت مدیریتی»: how far reality is from the plan today
export function gapInsight(series) {
    const fa = (value) => Math.abs(value).toLocaleString("fa-IR", {maximumFractionDigits: 1});
    if (!series.hasPlan) return {tone: "gray", title: "برای مقایسه با برنامه، موعد هدف را تعیین کنید.", text: "بدون موعد، مشخص نیست تا امروز چه مقدار پیشرفت انتظار می‌رفت."};
    if (series.actualToday === null) return {tone: "gray", title: "هنوز پیشرفتی ثبت نشده است.", text: `طبق برنامه تا امروز باید ${fa(series.plannedToday)}٪ پیشرفت می‌داشت.`};
    const detail = `طبق برنامه تا امروز باید ${fa(series.plannedToday)}٪ پیشرفت می‌داشت؛ پیشرفت واقعی ${fa(series.actualToday)}٪ است.`;
    if (series.gap >= 1) return {tone: series.gap > 10 ? "red" : "amber", title: `پیشرفت واقعی ${fa(series.gap)} واحد درصد از برنامه عقب‌تر است.`, text: detail};
    if (series.gap <= -1) return {tone: "green", title: `پیشرفت واقعی ${fa(series.gap)} واحد درصد از برنامه جلوتر است.`, text: detail};
    return {tone: "green", title: "پیشرفت واقعی مطابق برنامه است.", text: detail};
}

export default function GoalProgressPanel({goalData, onChanged}) {
    const translate = useTranslate();
    const navigate = useNavigate();
    const goalKey = goalData?.documentId;
    const [progress, setProgress] = useState(null);
    const [failed, setFailed] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [updateOpen, setUpdateOpen] = useState(false);

    const load = useCallback(async () => {
        if (!goalKey) return;
        try {
            setProgress(await goalProgressApi.get(goalKey));
            setFailed(false);
        } catch (error) {
            setFailed(true);
        }
    }, [goalKey]);
    useEffect(() => { load(); }, [load]);

    const changed = (next) => {
        setProgress(next);
        onChanged?.(next);
    };

    if (failed) return <div className="gp-card p-3 text-xs gp-muted">پیشرفت هدف بارگذاری نشد. <button type="button" className="gp-link" onClick={load}>تلاش دوباره</button></div>;
    if (!progress) return <div className="gp-card p-3 text-xs gp-muted text-center">در حال بارگذاری پیشرفت…</div>;

    const settings = progress.settings;
    const manual = settings?.source === "manual" ? settings.manual : null;
    const source = SOURCES.find((item) => item.value === settings?.source);
    const measure = PROJECT_MEASURES.find((item) => item.value === settings?.projectMeasure);
    const unitLabel = manual ? UNITS.find((item) => item.value === manual.unit)?.label : null;
    const daysLeft = progress.daysLeft;
    const dueText = daysLeft === null ? "بدون موعد" : daysLeft > 0 ? `${daysLeft.toLocaleString("fa-IR")} روز تا موعد` : daysLeft === 0 ? "موعد امروز است" : `${Math.abs(daysLeft).toLocaleString("fa-IR")} روز از موعد گذشته`;

    return (
        <div className="gp-panel" dir="rtl">
            <div className="gp-cards">
                <div className="gp-card gp-stat">
                    <span className="gp-muted">تکمیل هدف</span>
                    <b className="gp-big">{formatPercent(progress.percent)}</b>
                    <span className={`text-xs ${daysLeft !== null && daysLeft < 0 ? "gp-error" : ""}`}>{dueText}</span>
                </div>
                <div className="gp-card gp-stat">
                    <span className="gp-muted">{manual ? "مقدار فعلی" : "منبع پیشرفت"}</span>
                    {manual ? (
                        <>
                            <b className="gp-big">{formatValue(manual.current, manual.unit, manual.decimals)}</b>
                            <span className="text-xs gp-muted" dir="rtl">{`${formatValue(manual.start, manual.unit, manual.decimals)} ← ${formatValue(manual.target, manual.unit, manual.decimals)}`}</span>
                        </>
                    ) : settings ? (
                        <>
                            <b className="text-base">{source?.label}{settings.source === "projects" ? ` · ${measure?.value === "tasks" ? "کارها" : measure?.value === "milestones" ? "مایلستون‌ها" : "پیشرفت کلی"}` : ""}</b>
                            <span className="text-xs gp-muted">{progress.percent === null ? "هنوز چیزی برای سنجش نیست" : detailText(progress.detail)}</span>
                        </>
                    ) : (
                        <>
                            <b className="text-base">تعیین نشده</b>
                            <span className="text-xs gp-muted">پیشرفت از به‌روزرسانی‌های هدف</span>
                        </>
                    )}
                </div>
                <div className="gp-card gp-stat">
                    <span className="gp-muted">آخرین وضعیت</span>
                    <b className="text-base">{progress.status?.goalStatus ? translate(progress.status.goalStatus) : "بدون وضعیت"}</b>
                    {progress.canManage
                        ? <button type="button" className="gp-link text-xs" onClick={() => navigate(`/goals/${goalKey}/update-status`)}>ثبت وضعیت</button>
                        : <span className="text-xs gp-muted">{progress.status?.lastUpdatedAt ? fullDate(String(progress.status.lastUpdatedAt).slice(0, 10)) : ""}</span>}
                </div>
            </div>

            <div className="gp-card p-3">
                <div className="flex align-items-center justify-content-between gap-2 flex-wrap mb-2">
                    <div>
                        <h3 className="gp-h m-0">روند تحقق هدف</h3>
                        <span className="gp-muted">
                            {settings ? `منبع: ${source?.label}${settings.source === "projects" ? ` (${measure?.label})` : ""}${manual ? ` · واحد: ${unitLabel}` : ""}` : "منبع پیشرفت تعیین نشده؛ نمودار از درصدهای ثبت‌شده در به‌روزرسانی‌های هدف است."}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        {manual && progress.canManage && <button type="button" className="gp-btn" onClick={() => setUpdateOpen(true)}><Icon icon="mi mi:add" className="text-sm"/>به‌روزرسانی پیشرفت</button>}
                        {progress.canManage && <button type="button" className="gp-btn ghost" onClick={() => setSettingsOpen(true)}><Icon icon="mi mi:tune" className="text-sm"/>تنظیمات پیشرفت</button>}
                    </div>
                </div>
                <ProgressChart progress={progress}/>
            </div>

            <GoalProgressSettings visible={settingsOpen} onHide={() => setSettingsOpen(false)} goal={goalKey} progress={progress} onSaved={changed}/>
            {manual && <UpdateProgressDialog visible={updateOpen} onHide={() => setUpdateOpen(false)} goal={goalKey} progress={progress} onSaved={changed}/>}
        </div>
    );
}

function ProgressChart({progress}) {
    const manual = progress?.settings?.source === "manual" ? progress.settings.manual : null;
    const series = useMemo(() => chartSeries(progress), [progress]);
    const insight = gapInsight(series);
    const data = useMemo(() => ({
        labels: series.labels,
        datasets: [
            {label: "پیشرفت واقعی", data: series.actual, borderColor: ACTUAL_COLOR, backgroundColor: ACTUAL_COLOR, borderWidth: 2.5, pointRadius: 4, pointHoverRadius: 6, pointBorderColor: "#fff", pointBorderWidth: 1.5, tension: 0.15, spanGaps: true},
            ...(series.hasPlan ? [{label: "پیشرفت برنامه‌ریزی‌شده", data: series.planned, borderColor: PLAN_COLOR, backgroundColor: PLAN_COLOR, borderWidth: 2.5, pointRadius: 4, pointHoverRadius: 6, pointBorderColor: "#fff", pointBorderWidth: 1.5, tension: 0.15}] : []),
        ],
    }), [series]);
    const options = useMemo(() => ({
        maintainAspectRatio: false,
        animation: false,
        layout: {padding: {top: 18}},
        interaction: {mode: "index", intersect: false},
        plugins: {
            legend: {display: false},
            goalProgressToday: {index: series.todayIndex},
            tooltip: {
                rtl: true,
                textDirection: "rtl",
                titleFont: {family: CHART_FONT},
                bodyFont: {family: CHART_FONT},
                callbacks: {
                    title: (items) => {
                        const point = series.points[items[0]?.dataIndex];
                        return point ? `${point.label} · ${shortDate(point.day)}` : "";
                    },
                    label: (item) => {
                        const unitValue = item.datasetIndex === 0 && manual ? series.values[item.dataIndex] : null;
                        return `${item.dataset.label}: ${formatPercent(item.raw)}${unitValue !== null && unitValue !== undefined ? ` (${formatValue(unitValue, manual.unit, manual.decimals)})` : ""}`;
                    },
                },
            },
        },
        scales: {
            x: {grid: {display: false}, ticks: {font: {family: CHART_FONT, size: 10}, color: "#78716c", autoSkip: false, maxRotation: 0}},
            y: {min: 0, max: 100, grid: {color: "#f0eeec", borderDash: [4, 4]}, ticks: {font: {family: CHART_FONT, size: 10}, color: "#a8a29e", stepSize: 25, callback: (value) => formatPercent(value)}},
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [series, manual]);
    return (
        <div className="flex flex-column gap-3">
            <div className="gp-legend">
                <span><span className="gp-dot" style={{background: ACTUAL_COLOR}}/>پیشرفت واقعی</span>
                {series.hasPlan && <span><span className="gp-dot" style={{background: PLAN_COLOR}}/>پیشرفت برنامه‌ریزی‌شده</span>}
            </div>
            <div className="gp-chart">
                <Chart type="line" data={data} options={options} plugins={[todayLine]} style={{height: "100%"}}/>
            </div>
            <div className={`gp-insight tone-${insight.tone}`}>
                <span className="gp-muted">برداشت مدیریتی</span>
                <b className="text-sm">{insight.title}</b>
                <span className="text-xs text-700">{insight.text}</span>
            </div>
        </div>
    );
}

function UpdateProgressDialog({visible, onHide, goal, progress, onSaved}) {
    const manual = progress.settings.manual;
    const [value, setValue] = useState("");
    const [day, setDay] = useState(progress.today);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!visible) return;
        setValue(String(manual.current ?? ""));
        setDay(progress.today);
        setError("");
        setSaving(false);
    }, [visible, manual, progress.today]);
    const number = parseNumber(value);
    const preview = Number.isFinite(number) ? manualPercent(manual, number) : null;
    const save = async () => {
        setError("");
        if (!Number.isFinite(number)) return setError("مقدار فعلی را وارد کنید.");
        if (day > progress.today) return setError("تاریخ ثبت نمی‌تواند در آینده باشد.");
        setSaving(true);
        try {
            onSaved(await goalProgressApi.addEntry(goal, {value: number, date: day}));
            onHide();
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setSaving(false);
        }
    };
    return (
        <Dialog visible={visible} onHide={onHide} modal dir="rtl" className="rtl w-11 md:w-6 lg:w-4"
                header={<b className="text-base">به‌روزرسانی پیشرفت</b>}
                footer={(
                    <div className="flex justify-content-end gap-2">
                        <button type="button" className="gp-btn" onClick={onHide}>انصراف</button>
                        <button type="button" className="gp-btn primary" disabled={saving || !Number.isFinite(number)} onClick={save}>{saving ? "در حال ذخیره…" : "ذخیره"}</button>
                    </div>
                )}>
            <div className="grid m-0 gap-3">
                <label className="col p-0 gp-field"><span>مقدار فعلی <b className="gp-req">*</b></span>
                    <InputText autoFocus value={value} onChange={(event) => setValue(event.target.value)} inputMode="decimal" className="w-full text-sm ltr-input"
                               onKeyDown={(event) => event.key === "Enter" && save()} aria-label="مقدار فعلی"/>
                </label>
                <label className="col p-0 gp-field">تاریخ ثبت
                    <DayPicker value={day} onChange={(next) => setDay(next || progress.today)}/>
                </label>
            </div>
            <p className="gp-muted m-0 mt-3">
                {`${formatValue(manual.start, manual.unit, manual.decimals)} ← ${formatValue(manual.target, manual.unit, manual.decimals)}`}
                {preview !== null ? ` · با این مقدار: ${formatPercent(preview)} تکمیل` : ""}
            </p>
            {error && <p className="text-xs gp-error m-0 mt-2" role="alert">{error}</p>}
        </Dialog>
    );
}
